import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CampusAmbassadorProgram = ({ ambassadorData, referrals, challenges }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    })?.format(amount);
  };

  const getLevelBadge = (level) => {
    const badges = {
      'bronze': { color: 'var(--color-warning)', icon: 'Award' },
      'silver': { color: 'var(--color-muted-foreground)', icon: 'Award' },
      'gold': { color: 'var(--color-accent)', icon: 'Crown' }
    };
    return badges?.[level] || badges?.bronze;
  };

  const getProgressPercentage = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: 'BarChart3' },
    { id: 'referrals', label: 'Referencias', icon: 'Users' },
    { id: 'challenges', label: 'Desafíos', icon: 'Target' }
  ];

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
            <Icon name="Users" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground">
              Programa Embajador
            </h3>
            <p className="text-sm text-muted-foreground">
              Gana créditos extra ayudando a otros estudiantes
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Icon 
            name={getLevelBadge(ambassadorData?.level)?.icon} 
            size={20} 
            color={getLevelBadge(ambassadorData?.level)?.color} 
          />
          <span className="font-medium text-foreground capitalize">
            {ambassadorData?.level}
          </span>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-muted rounded-lg p-1">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth flex-1 justify-center ${
              activeTab === tab?.id
                ? 'bg-background text-foreground shadow-warm-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Users" size={18} color="var(--color-accent)" />
                <span className="text-sm font-medium text-foreground">Referencias Totales</span>
              </div>
              <p className="font-data font-bold text-2xl text-foreground">
                {ambassadorData?.totalReferrals}
              </p>
              <p className="text-xs text-muted-foreground">
                +{ambassadorData?.monthlyReferrals} este mes
              </p>
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Coins" size={18} color="var(--color-success)" />
                <span className="text-sm font-medium text-foreground">Créditos Ganados</span>
              </div>
              <p className="font-data font-bold text-2xl text-success">
                {formatCurrency(ambassadorData?.creditsEarned)}
              </p>
              <p className="text-xs text-muted-foreground">
                +{formatCurrency(ambassadorData?.monthlyCredits)} este mes
              </p>
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-foreground">Ranking Campus</span>
              </div>
              <p className="font-data font-bold text-2xl text-foreground">
                #{ambassadorData?.campusRank}
              </p>
              <p className="text-xs text-muted-foreground">
                de {ambassadorData?.totalAmbassadors} embajadores
              </p>
            </div>
          </div>

          {/* Next Level Progress */}
          <div className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-foreground">Progreso al Siguiente Nivel</h4>
              <span className="text-sm text-muted-foreground">
                {ambassadorData?.currentLevelProgress}/{ambassadorData?.nextLevelTarget}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2 mb-2">
              <div 
                className="bg-gradient-to-r from-accent to-primary h-2 rounded-full transition-all duration-500"
                style={{ 
                  width: `${getProgressPercentage(ambassadorData?.currentLevelProgress, ambassadorData?.nextLevelTarget)}%` 
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {ambassadorData?.nextLevelTarget - ambassadorData?.currentLevelProgress} referencias más para nivel {ambassadorData?.nextLevel}
            </p>
          </div>
        </div>
      )}
      {/* Referrals Tab */}
      {activeTab === 'referrals' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-foreground">Mis Referencias</h4>
            <Button variant="outline" size="sm">
              <Icon name="Share2" size={16} className="mr-2" />
              Compartir Código
            </Button>
          </div>

          <div className="bg-muted/30 rounded-lg p-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Tu Código de Referencia</p>
              <div className="flex items-center justify-center space-x-2">
                <code className="bg-background px-4 py-2 rounded-md font-mono text-lg font-bold text-foreground">
                  {ambassadorData?.referralCode}
                </code>
                <Button variant="ghost" size="icon">
                  <Icon name="Copy" size={16} />
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {referrals?.map((referral) => (
              <div key={referral?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                    {referral?.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{referral?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Se unió el {new Intl.DateTimeFormat('es-MX')?.format(referral?.joinDate)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-success">
                    +{formatCurrency(referral?.creditEarned)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {referral?.status === 'active' ? 'Activo' : 'Pendiente'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Challenges Tab */}
      {activeTab === 'challenges' && (
        <div className="space-y-4">
          <h4 className="font-medium text-foreground">Desafíos Comunitarios</h4>
          
          <div className="space-y-3">
            {challenges?.map((challenge) => (
              <div key={challenge?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon name={challenge?.icon} size={20} color="var(--color-accent)" />
                    <h5 className="font-medium text-foreground">{challenge?.title}</h5>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-accent">
                      {formatCurrency(challenge?.reward)}
                    </p>
                    <p className="text-xs text-muted-foreground">Premio</p>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mb-3">
                  {challenge?.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex-1 mr-4">
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-accent h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${getProgressPercentage(challenge?.progress, challenge?.target)}%` 
                        }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {challenge?.progress}/{challenge?.target} {challenge?.unit}
                    </p>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {challenge?.timeLeft} días restantes
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampusAmbassadorProgram;